import { router } from "@inertiajs/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { PriceRange } from "@/components/brand/settings/products/price-range"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { brandSettingsProductsPath } from "@/routes"
import type {
  CatalogPickerPayload,
  CatalogPickerProduct,
} from "@/types/missions"

/**
 * OKS-933 — catalog product picker (UX Spec §3).
 *
 * A `Sheet` containing a cmdk `Command`. Search is SERVER-backed: each
 * keystroke (debounced) issues
 * `router.reload({only: ["catalog_products"], data: {product_q}})`, because the
 * stated catalogue scale is 2000 SKUs and shipping all of them to filter
 * client-side is not viable.
 *
 * # ⚠️ `shouldFilter={false}` is required, not stylistic
 *
 * cmdk filters its items by default. The list arriving here is ALREADY
 * server-filtered, so leaving cmdk's filter on double-filters: the server
 * returns rows matching "hood", cmdk then re-scores them against the same
 * string with its own fuzzy algorithm and can hide rows the server chose. The
 * symptom is "search returns fewer results than it should", which reads as a
 * backend bug.
 *
 * # ⚠️ The typed-mission-form-state hazard (OQ-3)
 *
 * `router.reload` is a partial visit against the SAME url. The mission form's
 * inputs are uncontrolled (`defaultValue`, applied only at mount), so anything
 * the operator already typed survives ONLY while the fieldset subtree stays
 * mounted.
 *
 * That looked like three independent risks. It is TWO — `preserveState` is
 * guaranteed by construction on a reload (see the note at the `router.reload`
 * call). The two that remain: the fieldset's `key`
 * (`gift-product-${typeSpecificResetKey}`) staying stable across the visit, and
 * Inertia merging `catalog_products` without replacing the `mission` prop.
 *
 * ⚠️ Do NOT let `product_q` participate in that key, and do not lift the query
 * into a parent that re-keys the fieldset. A Vitest test cannot catch a
 * regression here — it mocks the router and so supplies the very mechanism
 * under test. `tests/playwright/oks933-picker-state.spec.ts` is the arbiter.
 */
export interface MissionProductPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Deferred prop; `undefined` until Inertia resolves it. */
  catalog: CatalogPickerPayload | undefined
  onSelect: (product: CatalogPickerProduct) => void
}

const SEARCH_DEBOUNCE_MS = 250

export function MissionProductPicker({
  open,
  onOpenChange,
  catalog,
  onSelect,
}: MissionProductPickerProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState("")
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const reload = useCallback((product_q: string) => {
    // ⚠️ `preserveState` / `preserveScroll` are deliberately NOT passed — they
    // cannot be. `ReloadOptions` is
    // `Omit<VisitOptions, 'preserveScroll' | 'preserveState'>`, and at runtime
    // `doReload` spreads the caller's options FIRST and then hard-sets both to
    // `true` (`@inertiajs/core` `doReload`). So state preservation on a reload
    // is guaranteed BY CONSTRUCTION, not by this call site — nobody can turn it
    // off here, by accident or otherwise.
    //
    // That is stronger than a test could be, and it retires one of OQ-3's three
    // failure modes outright. The two that remain are real and still need the
    // Playwright verdict: the fieldset's `key` staying stable across the visit,
    // and Inertia merging `catalog_products` without replacing `mission`.
    router.reload({
      only: ["catalog_products"],
      data: { product_q },
    })
  }, [])

  useEffect(() => {
    // Only the debounce timer is cleaned up here; the visit itself is
    // idempotent and a late response simply replaces the list.
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const onQueryChange = (value: string) => {
    setQuery(value)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => reload(value), SEARCH_DEBOUNCE_MS)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 sm:max-w-md"
        data-testid="catalog-picker-sheet"
      >
        <SheetHeader>
          {/* a11y — a dialog must have an accessible name (UX §7). */}
          <SheetTitle>{t("missions.form.product.picker.title")}</SheetTitle>
          <SheetDescription className="sr-only">
            {t("missions.form.product.picker.search_label")}
          </SheetDescription>
        </SheetHeader>

        {/* ⚠️ `shouldFilter={false}` — the list is already server-filtered. */}
        <Command shouldFilter={false} className="flex-1 overflow-hidden">
          <CommandInput
            value={query}
            onValueChange={onQueryChange}
            aria-label={t("missions.form.product.picker.search_label")}
            placeholder={t("missions.form.product.picker.search_placeholder")}
            data-testid="catalog-picker-search"
          />
          <CommandList>
            <PickerBody
              catalog={catalog}
              query={query}
              onSelect={(product) => {
                onSelect(product)
                onOpenChange(false)
              }}
            />
          </CommandList>
        </Command>
      </SheetContent>
    </Sheet>
  )
}

/**
 * ⚠️ The empty states are NOT interchangeable, and an empty `products` array
 * cannot tell them apart — P1 (no catalogue), P3 (query matched nothing) and
 * P5 (everything archived) all produce zero rows. `has_any` / `has_active`
 * are what discriminate; rendering `CommandEmpty` for all three would tell a
 * brand with 400 archived products that no product matches their search.
 */
function PickerBody({
  catalog,
  query,
  onSelect,
}: {
  catalog: CatalogPickerPayload | undefined
  query: string
  onSelect: (product: CatalogPickerProduct) => void
}) {
  const { t } = useTranslation()

  // Deferred prop not resolved yet — not an empty state.
  if (!catalog) {
    return (
      <div className="text-muted-foreground p-4 text-sm" aria-busy="true">
        {t("missions.form.product.picker.search_label")}
      </div>
    )
  }

  // P1 — no products at all. A store-less brand adding one by hand is the
  // ordinary OKS-932 path, so this links out rather than reading as an error.
  if (!catalog.has_any) {
    return (
      <EmptyPanel
        testId="catalog-picker-p1"
        title={t("missions.form.product.picker.none_title")}
        href={brandSettingsProductsPath()}
        cta={t("missions.form.product.picker.none_cta")}
      />
    )
  }

  // ⚠️ P2 is checked ONLY once we know there is nothing to show. It used to
  // sit ahead of the list and HID a populated catalogue: a brand with a
  // connected-but-never-synced store and manual products (OKS-932's ordinary
  // path — UX §3's P4) saw "your catalogue hasn't synced yet" while holding
  // active products. PO changed one variable (`last_synced_at`) and the list
  // appeared.
  //
  // A pending sync explains why a list is EMPTY; it cannot justify hiding one.
  // `products.length > 0` always renders the list, whatever the store says.
  if (catalog.products.length > 0) {
    return (
      <>
        {catalog.products.map((product) => (
          <PickerRow key={product.id} product={product} onSelect={onSelect} />
        ))}
        {/* ⚠️ A silent cap reads as "you don't have that product". */}
        {catalog.truncated && (
          <p
            className="text-muted-foreground px-3 py-2 text-xs"
            data-testid="catalog-picker-truncated"
          >
            {t("missions.form.product.picker.truncated", {
              count: catalog.products.length,
            })}
          </p>
        )}
      </>
    )
  }

  // Nothing to show. Now the store state explains WHY.
  if (catalog.store?.never_synced) {
    return (
      <EmptyPanel
        testId="catalog-picker-p2"
        title={t("missions.form.product.picker.unsynced_title")}
        body={
          catalog.store.sync_in_progress
            ? t("missions.form.product.picker.syncing_body")
            : t("missions.form.product.picker.unsynced_body")
        }
      />
    )
  }

  // P5 — the brand has products but every one is archived. Archived products
  // are NEVER listed (AC2), so this must be its own screen.
  if (!catalog.has_active) {
    return (
      <EmptyPanel
        testId="catalog-picker-p5"
        title={t("missions.form.product.picker.all_archived_title")}
        href={`${brandSettingsProductsPath()}?archived=1`}
        cta={t("missions.form.product.picker.all_archived_cta")}
      />
    )
  }

  // P3 — genuine no-match. Only reachable once we know the brand HAS active
  // products, which is why the checks above come first.
  if (catalog.products.length === 0) {
    return (
      <CommandEmpty data-testid="catalog-picker-p3">
        {t("missions.form.product.picker.empty", { query })}
      </CommandEmpty>
    )
  }

  // Unreachable: `products.length > 0` returned above and every empty case is
  // handled. Kept as a total function rather than an implicit undefined.
  return null
}

function PickerRow({
  product,
  onSelect,
}: {
  product: CatalogPickerProduct
  onSelect: (product: CatalogPickerProduct) => void
}) {
  const { t } = useTranslation()

  return (
    <CommandItem
      value={String(product.id)}
      onSelect={() => onSelect(product)}
      className="flex items-center gap-3"
      data-testid="catalog-picker-row"
    >
      {/* ⚠️ Reuses the Produits row SHAPE but not its `table-fixed` layout —
          that surface is `<BrandSettingsLayout wide>` where the widths are
          caps; the Sheet is narrow and a fixed row truncates here (UX §3). */}
      {product.image_url ? (
        <img
          src={product.image_url}
          alt=""
          className="size-10 shrink-0 rounded object-cover"
        />
      ) : (
        <span
          className="bg-muted size-10 shrink-0 rounded"
          aria-hidden="true"
        />
      )}

      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium">{product.title}</span>
        <span className="text-muted-foreground text-xs">
          <PriceRange
            minCents={product.min_price_cents}
            maxCents={product.max_price_cents}
            currency={product.currency}
          />
          {" · "}
          {t("missions.form.product.picker.variant_count", {
            count: product.variant_count,
          })}
        </span>
      </span>

      {product.manual && (
        <Badge variant="secondary" className="ml-auto shrink-0">
          {t("missions.form.product.picker.manual_marker")}
        </Badge>
      )}
    </CommandItem>
  )
}

function EmptyPanel({
  testId,
  title,
  body,
  href,
  cta,
}: {
  testId: string
  title: string
  body?: string
  href?: string
  cta?: string
}) {
  return (
    <div className="flex flex-col items-start gap-2 p-4" data-testid={testId}>
      <p className="text-sm font-medium">{title}</p>
      {body && <p className="text-muted-foreground text-xs">{body}</p>}
      {href && cta && (
        <Button variant="link" className="h-auto p-0" asChild>
          <a href={href}>{cta}</a>
        </Button>
      )}
    </div>
  )
}

export default MissionProductPicker
